/**
 * Route-specific error boundary component
 */
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";

/**
 * Default error boundary for routes
 * Provides a consistent error UI with retry functionality
 *
 * `ErrorComponentProps["error"]` is `unknown` -- a route can throw any value,
 * not just an `Error` -- so the message is read only after narrowing.
 */
export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  return (
    <div className="p-8 text-center">
      <h2 className="text-xl font-bold mb-4">Something went wrong</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        {(error instanceof Error && error.message) || "An error occurred"}
      </p>
      <div className="flex gap-4">
        <button
          className="px-4 py-2 bg-theme-primary text-white rounded hover:bg-theme-primary/90"
          onClick={reset}
          type="button"
        >
          Try Again
        </button>
        <Link
          className="inline-block px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
          to="/"
        >
          Go Home
        </Link>
      </div>
    </div>
  );
}
